import { SdsLocale, type SDSDocument } from "../../data/models/schemas";
import { SDS_ENABLE_ADDITIONAL_JURISDICTIONS } from "../utilities/FeatureFlags";
import { getSdsLocaleLanguageTag } from "./sdsApiContract";

export type SdsLocaleOption = {
    id: string;
    locale: SdsLocale;
    flag: string;
    countryCode: string;
    countryLabelKey: string;
    languageCode: string;
    languageLabelKey: string;
    enabled: boolean;
};

export const DEFAULT_SDS_LOCALE_ID = "us-en";

export const SDS_LOCALE_OPTIONS: SdsLocaleOption[] = [
    {
        id: "us-en",
        locale: SdsLocale.EN_US,
        flag: "🇺🇸",
        countryCode: "US",
        countryLabelKey: "sds-locale-country-usa",
        languageCode: "en-US",
        languageLabelKey: "sds-locale-language-english",
        enabled: true,
    },
    {
        id: "ca-en",
        locale: SdsLocale.EN_CA,
        flag: "🇨🇦",
        countryCode: "CA",
        countryLabelKey: "sds-locale-country-canada",
        languageCode: "en-CA",
        languageLabelKey: "sds-locale-language-english",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "ca-fr",
        locale: SdsLocale.FR_CA,
        flag: "🇨🇦",
        countryCode: "CA",
        countryLabelKey: "sds-locale-country-canada",
        languageCode: "fr-CA",
        languageLabelKey: "sds-locale-language-french",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "de-de",
        locale: SdsLocale.DE_DE,
        flag: "🇩🇪",
        countryCode: "DE",
        countryLabelKey: "sds-locale-country-germany",
        languageCode: "de-DE",
        languageLabelKey: "sds-locale-language-german",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "au-en",
        locale: SdsLocale.EN_AU,
        flag: "🇦🇺",
        countryCode: "AU",
        countryLabelKey: "sds-locale-country-australia",
        languageCode: "en-AU",
        languageLabelKey: "sds-locale-language-english",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
    {
        id: "jp-ja",
        locale: SdsLocale.JA_JP,
        flag: "🇯🇵",
        countryCode: "JP",
        countryLabelKey: "sds-locale-country-japan",
        languageCode: "ja-JP",
        languageLabelKey: "sds-locale-language-japanese",
        get enabled() {
            return SDS_ENABLE_ADDITIONAL_JURISDICTIONS();
        },
    },
];

export const getSdsLocaleOption = (localeId?: string) => {
    return (
        SDS_LOCALE_OPTIONS.find((option) => option.id === localeId) ??
        SDS_LOCALE_OPTIONS[0]
    );
};

export const getSdsLocaleOptionForLocale = (locale: SdsLocale) =>
    SDS_LOCALE_OPTIONS.find((option) => option.locale === locale) ??
    SDS_LOCALE_OPTIONS[0];

export const getSdsLocaleOptionForLanguage = (languageCode?: string) => {
    const normalized = languageCode?.toLowerCase();
    return (
        SDS_LOCALE_OPTIONS.find(
            (option) => option.languageCode.toLowerCase() === normalized
        ) ?? SDS_LOCALE_OPTIONS[0]
    );
};

export const getSdsLocaleOptions = (locales?: SdsLocale[]) => {
    if (!locales?.length) {
        return SDS_LOCALE_OPTIONS;
    }
    const allowed = new Set(locales);
    return SDS_LOCALE_OPTIONS.filter((option) => allowed.has(option.locale));
};

export const getLocalizedLabelContentKey = (languageCode: string) => {
    const normalizedLanguageCode = languageCode.trim().toLowerCase();
    if (!normalizedLanguageCode) {
        return "en";
    }

    return normalizedLanguageCode.split("-")[0];
};

/**
 * Output language is part of profile_id and cannot be changed independently
 * after generation. Retained for compatibility with label/editor call sites.
 */
export const applyLocaleToSdsDocument = (
    sdsDocument: SDSDocument,
    languageCode: string
) => {
    void languageCode;
    return sdsDocument;
};

export const backendLocaleLanguageCode = (locale: SdsLocale) =>
    getSdsLocaleLanguageTag(locale);
